import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { FaMapMarkerAlt } from 'react-icons/fa'; 
import { useGLTF, OrbitControls } from '@react-three/drei';
import './AboutMe.css';
import { Bloom, EffectComposer, Pixelation } from '@react-three/postprocessing';

import * as THREE from 'three';

function Kelptocat() {
  const modelRef = useRef();
  const mouse = useRef(new THREE.Vector2());
  const { viewport, camera } = useThree();
  const { scene } = useGLTF('/Kleptocat.glb');

  const MAX_SCALE = 50;

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (event) => {
      mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [scene]);

  useFrame((state) => {
    if (modelRef.current) {
      // Floating + spin
      const elapsedTime = state.clock.getElapsedTime();
      modelRef.current.position.y = Math.sin(elapsedTime * 0.5) * 0.1;

      // Adjust scale
      let scale = Math.min(viewport.width, viewport.height) * 2.5;
      if (scale > MAX_SCALE) scale = MAX_SCALE;
      modelRef.current.scale.set(scale, scale, scale);

      // Calculate mouse target
      const vector = new THREE.Vector3(mouse.current.x, mouse.current.y, 0.5);
      vector.unproject(camera);

      const dir = vector.sub(camera.position).normalize();
      const distance = 2;
      const target = camera.position.clone().add(dir.multiplyScalar(distance));

      // Apply base rotation offset to correct the model's default orientation
      // Then make it look at the target
      const baseRotationY = -Math.PI / 2; // Adjust this value until it faces forward
      
      // Calculate the direction to look at
      const direction = new THREE.Vector3();
      direction.subVectors(target, modelRef.current.position).normalize();
      
      // Calculate the horizontal angle (left/right)
      const horizontalAngle = Math.atan2(direction.x, direction.z);
      
      // Calculate the vertical angle (up/down)
      const verticalAngle = Math.asin(direction.y);
      
      // Apply the rotations
      modelRef.current.rotation.y = baseRotationY + horizontalAngle;
      modelRef.current.rotation.x = -verticalAngle; // Negative because of coordinate system
    }
  });

  return (
    <primitive
      ref={modelRef}
      object={scene}
      position={[0, 0, 0]}
    />
  );
}

function ResponsiveCanvas() {
  const { camera, size } = useThree();

  useEffect(() => {
    camera.aspect = size.width / size.height;
    camera.updateProjectionMatrix();

    if (size.width < 768) {
      camera.position.z = 5;
    } else {
      camera.position.z = 4.5;
    }
  }, [camera, size]);

  return null;
}

export default function AboutMe() {
  return (
    <div className="about-me-container">
      <div className="about-me-text">
        <h1>Hi! I'm Yazid,</h1>
        <p>Computer Engineering student at McGill University with a strong foundation in full-stack, cloud and DevOps.</p>
        <p>I love video games and I'm curious about combining machine learning with game development. Currently learning computer graphics to understand how games work under the hood. I enjoy experimenting with 3D, building UIs, and tinkering with game mechanics. Also, cats make everything better.</p>
        <p>
          <FaMapMarkerAlt className="location-icon" /> 
          Montreal, Canada
        </p>
        <a 
          href="/resume.pdf" 
          download="Yazid_Asselah_Resume.pdf" 
          className="download-button">
          Download My Resume
        </a>
      </div>
      
      <div className="about-me-canvas">
        <Canvas camera={{ fov: 75, near: 0.1, far: 100, position: [0, 1, 3] }}>
          <EffectComposer>
            <Bloom />
            <Pixelation granularity={3} />
          </EffectComposer>

          <directionalLight castShadow position={[0, 3, 1]} intensity={2} />
          <ambientLight intensity={0.5} />
          <Kelptocat />
          <ResponsiveCanvas />
        </Canvas>
      </div>
    </div>
  );
}

// Preload the model
useGLTF.preload('/Kleptocat.glb');